import React from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';

import { BasePopupActionPayload, BasePopupProps } from 'contexts/PopupContext';
import { MirrorService } from 'services/MirrorService';

import { useMedia } from 'hooks/useMedia';

import { BaseButton, ButtonTypes } from 'components/base/BaseButton';
import { FilesReader } from 'components/shared/FilesReader';
import { Popup } from 'components/UI/Popup';

import { useUploadPhotoPopupBL } from './hooks/useUploadPhotoPopupBL';
import { ReactComponent as AddPhotoIcon } from './images/add-photo.svg';
import { ReactComponent as AddPhotoIconThemeMePlusYou } from './images/add-photo_meplusyou.svg';

import css from './uploadPhotoPopup.module.sass';

type ActionsPayload = BasePopupActionPayload<'close'>;

type Props = BasePopupProps<null, (action: ActionsPayload) => void>;

export const UploadPhotoPopup: React.FC<Props> = ({ onAction }) => {
  const { isMobile } = useMedia();
  const { t } = useTranslation(['common', 'app']);

  const handleClose = () => {
    onAction({ type: 'close' });
  };

  const { isLoading, errorMessage, handleUploadPhoto } = useUploadPhotoPopupBL({
    onClose: handleClose,
  });

  return (
    <Popup isFullScreen={isMobile}>
      <div className={css.root}>
        <main className={css.main}>
          {MirrorService.isMePlusYou ? (
            <AddPhotoIconThemeMePlusYou
              className={cx(css.icon, css.iconThemeMePlusYou)}
            />
          ) : (
            <AddPhotoIcon className={css.icon} />
          )}

          <h3 className={css.title}>{t('app:uploadPhotoPopup.title')}</h3>

          <p className={css.description}>
            {t('app:uploadPhotoPopup.description')}
          </p>

          {errorMessage && <p className={css.error}>{errorMessage}</p>}
        </main>

        <ul className={css.actions}>
          <li className={css.action}>
            <FilesReader
              multiple={false}
              onLoadFinish={({ file }) => handleUploadPhoto(file)}
            >
              <BaseButton
                className={css.uploadBtn}
                type={ButtonTypes.Accent}
                loading={isLoading}
                // TODO fix this hack add as prop to button component
                to="/"
                onClick={(e) => e.preventDefault()}
              >
                {t('app:uploadPhotoPopup.title')}
              </BaseButton>
            </FilesReader>
          </li>

          <li className={css.action}>
            <BaseButton type={ButtonTypes.Secondary} onClick={handleClose}>
              {t('common:skip')}
            </BaseButton>
          </li>
        </ul>
      </div>
    </Popup>
  );
};
